// Yoj dispatch: the UK dispatch schedule (cut-off, dispatch days, bank holidays, closures), delivery estimates,
// and the <yoj-dispatch-countdown> element. The pure functions are exported for the tests.

const ZONE = 'Europe/London';
const DAY = 86400000;
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const londonFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: ZONE, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric',
});

// Calendar days are whole days since 1 January 1970, so date sums are plain arithmetic
export const toDay = (year, month, day) => Date.UTC(year, month - 1, day) / DAY;
export const weekdayOf = (day) => new Date(day * DAY).getUTCDay();
export const isoOf = (day) => new Date(day * DAY).toISOString().slice(0, 10);
const yearOf = (day) => new Date(day * DAY).getUTCFullYear();

// The London calendar day and minute of the day at an instant, whatever the visitor's own time zone
export function londonNow(instant) {
  const p = {};
  for (const { type, value } of londonFormat.formatToParts(instant)) if (type !== 'literal') p[type] = Number(value);
  return { day: toDay(p.year, p.month, p.day), minutes: p.hour * 60 + p.minute, seconds: p.second };
}

// The instant it is a given minute of a given day in London, allowing for British Summer Time
export function londonInstant(day, minutes) {
  const target = day * DAY + minutes * 60000;
  let guess = target;
  for (let i = 0; i < 3; i++) {
    const seen = londonNow(new Date(guess));
    const diff = target - (seen.day * DAY + seen.minutes * 60000 + seen.seconds * 1000);
    if (diff === 0) break;
    guess += diff;
  }
  return new Date(guess);
}

// Easter Sunday, by the anonymous Gregorian algorithm
export function easterSunday(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  return toDay(year, Math.floor((h + l - 7 * m + 114) / 31), ((h + l - 7 * m + 114) % 31) + 1);
}

const firstMonday = (year, month) => { const day = toDay(year, month, 1); return day + ((8 - weekdayOf(day)) % 7); };
const lastMonday = (year, month) => { const day = toDay(year, month + 1, 0); return day - ((weekdayOf(day) + 6) % 7); };

// England and Wales bank holidays by the standing rules, with substitute days.
// One-off changes (a moved May bank holiday, a royal occasion) go in the shop's own closed and open dates.
export function englandWalesBankHolidays(year) {
  const newYear = toDay(year, 1, 1), easter = easterSunday(year), christmas = toDay(year, 12, 25);
  const days = [weekdayOf(newYear) === 6 ? newYear + 2 : weekdayOf(newYear) === 0 ? newYear + 1 : newYear];
  days.push(easter - 2, easter + 1, firstMonday(year, 5), lastMonday(year, 5), lastMonday(year, 8));
  switch (weekdayOf(christmas)) {
    case 5: days.push(christmas, christmas + 3); break; // Boxing Day on a Saturday moves to Monday 28th
    case 6: days.push(christmas + 2, christmas + 3); break; // Monday 27th and Tuesday 28th
    case 0: days.push(christmas + 1, christmas + 2); break; // Boxing Day Monday 26th, Christmas Tuesday 27th
    default: days.push(christmas, christmas + 1);
  }
  return days;
}

const holidayCache = new Map();
export function isBankHoliday(day) {
  const year = yearOf(day);
  if (!holidayCache.has(year)) holidayCache.set(year, new Set(englandWalesBankHolidays(year)));
  return holidayCache.get(year).has(day);
}

// "2026-12-24, 2026-12-29..2026-12-31" (commas, spaces or new lines between entries) to a set of days
export function parseDates(text) {
  const days = new Set();
  for (const item of String(text ?? '').split(/[\s,;]+/)) {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:\.\.(\d{4})-(\d{2})-(\d{2}))?$/.exec(item);
    if (!m) continue;
    const from = toDay(+m[1], +m[2], +m[3]);
    const to = m[4] ? toDay(+m[4], +m[5], +m[6]) : from;
    for (let day = from; day <= to && day - from < 366; day++) days.add(day);
  }
  return days;
}

export function createSchedule({ cutoff = '13:00', days = '1,2,3,4,5', closed = '', open = '', bankHolidays = true } = {}) {
  const [h, m] = String(cutoff).trim().split(':').map(Number);
  const cutoffMinutes = h >= 0 && h < 24 && m >= 0 && m < 60 ? h * 60 + m : 13 * 60;
  const weekdays = new Set(String(days).split(/[\s,]+/).filter(Boolean).map(Number));
  const closedDays = parseDates(closed), openDays = parseDates(open);

  const isDispatchDay = (day) => {
    if (openDays.has(day)) return true;
    if (closedDays.has(day)) return false;
    return weekdays.has(weekdayOf(day)) && !(bankHolidays && isBankHoliday(day));
  };
  const nextDispatchDay = (after) => {
    for (let day = after + 1; day <= after + 60; day++) if (isDispatchDay(day)) return day;
    return null;
  };

  return {
    isDispatchDay,
    nextDispatchDay,
    // Before today's cut-off: { today: true, minutesLeft }. Otherwise the next dispatch day (null if none in 60 days).
    status(instant) {
      const now = londonNow(instant);
      if (isDispatchDay(now.day)) {
        const msLeft = londonInstant(now.day, cutoffMinutes) - instant;
        if (msLeft > 0) return { today: true, day: now.day, dispatchDay: now.day, minutesLeft: Math.ceil(msLeft / 60000) };
      }
      return { today: false, day: now.day, dispatchDay: nextDispatchDay(now.day) };
    },
  };
}

// The first and last likely delivery days, counting delivery days after dispatch:
// Monday to Friday (and Saturday if the service delivers then), never on bank holidays
export function deliveryWindow(dispatchDay, minDays, maxDays, { saturday = false } = {}) {
  const delivers = (day) => { const w = weekdayOf(day); return (w >= 1 && w <= 5 || (saturday && w === 6)) && !isBankHoliday(day); };
  const after = (count) => { let day = dispatchDay; for (let left = count; left > 0;) { day++; if (delivers(day)) left--; } return day; };
  return [after(minDays), after(Math.max(minDays, maxDays))];
}

export function formatDuration(minutes) {
  const h = Math.floor(minutes / 60), m = minutes % 60;
  const part = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  if (h && m) return `${part(h, 'hour')} ${part(m, 'minute')}`;
  return h ? part(h, 'hour') : part(m, 'minute');
}

// "tomorrow", "on Monday", or "on Tuesday 5 January" for a day a week or more away
export function describeDay(day, today) {
  if (day === today + 1) return 'tomorrow';
  const date = new Date(day * DAY), name = WEEKDAYS[date.getUTCDay()];
  return day - today < 7 ? `on ${name}` : `on ${name} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
}

// "Tue 13 – Thu 15 Oct", "Fri 30 Oct – Mon 2 Nov", or "Tue 13 Oct"
export function formatDateRange(from, to) {
  const label = (day, withMonth) => {
    const date = new Date(day * DAY);
    return `${WEEKDAYS[date.getUTCDay()].slice(0, 3)} ${date.getUTCDate()}${withMonth ? ' ' + MONTHS[date.getUTCMonth()].slice(0, 3) : ''}`;
  };
  if (from === to) return label(from, true);
  const sameMonth = new Date(from * DAY).getUTCMonth() === new Date(to * DAY).getUTCMonth() && to - from < 32;
  return `${label(from, !sameMonth)} – ${label(to, true)}`;
}

// Puts a template into an element as text, with its token's value in bold: no HTML from settings is ever parsed
function fill(element, template, token, value) {
  const [before, ...rest] = String(template).split(token);
  const strong = document.createElement('strong');
  strong.textContent = value;
  element.replaceChildren(before, ...(rest.length ? [strong, rest.join(token)] : []));
}

function defineElement() {
  if (typeof customElements === 'undefined' || customElements.get('yoj-dispatch-countdown')) return;

  class YojDispatchCountdown extends HTMLElement {
    // Inside a refreshed yoj panel the element is morphed in place, so it re-reads its settings when they change
    static observedAttributes = ['data-cutoff', 'data-days', 'data-closed', 'data-open', 'data-bank-holidays', 'data-available',
      'data-message-today', 'data-message-later', 'data-message-estimate', 'data-estimate-min', 'data-estimate-max', 'data-estimate-saturday'];

    connectedCallback() {
      this.configure();
      // Re-render on each minute boundary (the minutes shown change on the minute), and when a hidden tab comes back
      const untilNextMinute = () => 60050 - (Date.now() % 60000);
      this.tick = () => { this.render(); this.timer = setTimeout(this.tick, untilNextMinute()); };
      this.timer = setTimeout(this.tick, untilNextMinute());
      this.onVisibility = () => { if (!document.hidden) this.render(); };
      document.addEventListener('visibilitychange', this.onVisibility);
      // Standing alone, swap in the block as Shopify rendered it for a newly picked variant (sold out or not).
      // Inside a yoj panel, the panel does that for everything in it, then says when it's done, and this redraws.
      this.panel = this.closest('yoj-refresh');
      if (this.panel) {
        this.onPanelUpdated = () => this.render();
        this.panel.addEventListener('yoj:updated', this.onPanelUpdated);
        return;
      }
      this.section = this.closest('.shopify-section, dialog');
      this.onProductSelect = (event) => {
        event.promise?.then((result) => {
          const fresh = result?.detail?.html?.querySelector(`yoj-dispatch-countdown[data-block-id="${CSS.escape(this.dataset.blockId)}"]`);
          if (fresh && this.isConnected) this.replaceWith(document.importNode(fresh, true));
        }).catch(() => {});
      };
      this.section?.addEventListener('shopify:product:select', this.onProductSelect);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.section?.removeEventListener('shopify:product:select', this.onProductSelect);
      this.panel?.removeEventListener('yoj:updated', this.onPanelUpdated);
    }

    attributeChangedCallback() {
      if (this.isConnected && this.schedule) this.configure();
    }

    configure() {
      const data = this.dataset;
      this.schedule = createSchedule({ cutoff: data.cutoff, days: data.days, closed: data.closed, open: data.open, bankHolidays: data.bankHolidays !== 'false' });
      this.render();
    }

    render() {
      const data = this.dataset;
      const text = this.querySelector('[data-ref="text"]');
      const estimate = this.querySelector('[data-ref="estimate"]');
      const status = data.available === 'false' ? null : this.schedule.status(new Date());
      // Estimate-only (a faster delivery line): no countdown sentence, just the dates
      const estimateOnly = 'estimateOnly' in data;
      let show = false;
      if (estimateOnly) {
        show = status?.dispatchDay != null;
      } else if (status?.today) {
        fill(text, data.messageToday, '[time]', formatDuration(status.minutesLeft));
        show = true;
      } else if (status?.dispatchDay != null && data.messageLater) {
        fill(text, data.messageLater, '[day]', describeDay(status.dispatchDay, status.day));
        show = true;
      }
      if (estimate) {
        const min = parseInt(data.estimateMin, 10), max = parseInt(data.estimateMax, 10);
        const wanted = show && data.messageEstimate && min >= 0;
        if (wanted) {
          const [from, to] = deliveryWindow(status.dispatchDay, min, Number.isNaN(max) ? min : max, { saturday: data.estimateSaturday === 'true' });
          fill(estimate, data.messageEstimate, '[dates]', formatDateRange(from, to));
        }
        estimate.hidden = !wanted;
      }
      // In the theme editor, say why the block is hidden rather than vanish, so staff can still find and style it
      const editorNote = !show && 'designMode' in data && Boolean(text);
      if (editorNote) {
        text.textContent = data.available === 'false'
          ? 'Dispatch countdown: hidden for sold-out variants'
          : 'Dispatch countdown: hidden until the next dispatch day, before the cut-off';
      }
      this.classList.toggle('yoj-dispatch--editor-note', editorNote);
      this.hidden = !show && !editorNote;
    }
  }

  customElements.define('yoj-dispatch-countdown', YojDispatchCountdown);
}

defineElement();
